/**
 * Single source of truth for "the active Provider Portal bearer token, if
 * any." Used by two consumers that must never disagree:
 *
 *   - Member360ApolloProvider (forwards it as `x-pp-bearer` to
 *     care-management-gql's CBP resolvers).
 *   - MemberProfilePage's Create-Auth seamless PP login flow (ACT-1247
 *     follow-up) — decides whether to prompt for PP credentials before
 *     minting a Create-Auth handoff.
 *
 * A user's PP session lives in one of two places depending on how they
 * signed in:
 *   - selectedProvider === "provider-portal-b2c": their PRIMARY session
 *     token (from AuthContext) IS the PP token. No cross-identity risk to
 *     check — it's already scoped to whoever is currently signed in.
 *   - Entra / pathways-b2c: any PP session is a SEPARATE, side-by-side
 *     token stashed in b2cClient's namespaced localStorage (written by
 *     `ropcLogin` without touching AuthContext/selectedProvider — see
 *     PpLoginModal). This stash is NOT namespaced by identity, and while
 *     AuthContext.logout() now clears it on every logout (belt-and-
 *     suspenders), `getPpToken` independently verifies the stash's own
 *     BOUND identity email matches whoever is CURRENTLY signed in before
 *     ever trusting it — the guard against "log in once, log out, a
 *     different person signs in and inherits your PP identity."
 *
 *     That bound email is `stored.boundIdentityEmail` — the literal email
 *     the stash was authenticated with at ROPC-login time (PpLoginModal
 *     passes its LOCKED identity email straight through to `ropcLogin`,
 *     which persists it alongside the tokens; see b2cClient.ts). It is
 *     DELIBERATELY not derived by decoding the stash's `id_token`: Provider
 *     Portal's ROPC B2C policy is not guaranteed to emit an `emails` /
 *     `preferred_username` claim, so decoding would make this check (and
 *     therefore the whole feature) claim-shape-dependent — a token with no
 *     matching claim would read as "absent" even seconds after a successful
 *     login, reopening the login modal in a loop. Binding to the
 *     authenticated email instead makes the check work for ANY token shape.
 *
 * Fail-closed policy: a token is only trusted if we can POSITIVELY decode
 * its `exp` claim and confirm it's in the future. Undecodable/malformed
 * tokens and tokens with no `exp` claim are treated as ABSENT — never
 * "assume valid" for an auth-critical bearer we're about to forward.
 */

import type { AuthProviderId, B2cConfig } from "./authProviders";
import { providerPortalB2cConfig } from "./authProviders";
import { clearStoredTokens, getStoredTokens, ropcRefresh } from "./b2cClient";
import { jwtExpiryMs } from "./jwt";

const PROVIDER_PORTAL_B2C: AuthProviderId = "provider-portal-b2c";

/**
 * Resolved once at module load (env-var driven, same static-read discipline
 * as authProviders.ts) — independent of `featureFlags.loginProviderPortal`.
 */
export const ppB2cConfig: B2cConfig | null = providerPortalB2cConfig();
export const isPpB2cConfigured: boolean = ppB2cConfig !== null;

/**
 * Fail-closed freshness check. Only a token whose `exp` we can positively
 * decode and confirm is in the future is trusted — an undecodable/malformed
 * token is NOT assumed valid just because we can't prove it's expired.
 */
function freshOrUndefined(token: string | undefined): string | undefined {
  if (!token) return undefined;
  const expMs = jwtExpiryMs(token);
  if (expMs === null) return undefined; // undecodable / no exp claim
  if (expMs <= Date.now()) return undefined; // provably expired
  return token;
}

/**
 * Returns the active, non-expired, IDENTITY-VERIFIED Provider Portal bearer
 * token, or `undefined` if there isn't one.
 *
 * `currentIdentityEmail` is the email of whoever is CURRENTLY signed in
 * (Entra/Pathways/PP — see resolveIdentityEmail). For the stashed-token
 * branch this is REQUIRED to match the stash's own BOUND identity email
 * (`stored.boundIdentityEmail`, case-insensitive, trimmed — see the module
 * doc comment above) — a stash with no bound email, or a mismatched one, is
 * treated as absent. This is what prevents a stale/foreign stash from ever
 * being handed to a different signed-in identity.
 */
export function getPpToken(
  selectedProvider: AuthProviderId | null,
  sessionAccessToken: string | null,
  currentIdentityEmail: string | null | undefined,
): string | undefined {
  if (!isPpB2cConfigured) return undefined;

  if (selectedProvider === PROVIDER_PORTAL_B2C) {
    // Own session — this token IS whoever is currently signed in as PP.
    return freshOrUndefined(sessionAccessToken ?? undefined);
  }

  // Stashed side-by-side token — MUST be bound to the identity currently
  // signed in. Fail closed on any missing/unresolvable/mismatched claim.
  const wantEmail = currentIdentityEmail?.trim().toLowerCase();
  if (!wantEmail) return undefined;

  const stored = getStoredTokens(PROVIDER_PORTAL_B2C);
  if (!stored?.access_token) return undefined;

  const storedEmail = stored.boundIdentityEmail?.trim().toLowerCase();
  if (!storedEmail || storedEmail !== wantEmail) return undefined;

  return freshOrUndefined(stored.access_token);
}

/**
 * True if a PP B2C refresh token is stashed and bound to
 * `currentIdentityEmail` — i.e. this identity has completed the one-time PP
 * link (ACT-1247 SSO-PP auth-linking Phase 1 / Option A, design doc §3, §5).
 * Deliberately does NOT check freshness: an expired-but-refreshable stash
 * still counts as "linked" (that's the whole point of persisting the
 * refresh token) — only a genuinely absent/foreign/refresh-token-less stash
 * reads as unlinked. Synchronous — safe to call at render time to decide
 * "offer link" vs "run the eligibility pre-flight" (see
 * members/usePpCreateAuthPreflight.ts).
 */
export function hasPersistedPpLink(
  currentIdentityEmail: string | null | undefined,
): boolean {
  if (!isPpB2cConfigured) return false;
  const wantEmail = currentIdentityEmail?.trim().toLowerCase();
  if (!wantEmail) return false;
  const stored = getStoredTokens(PROVIDER_PORTAL_B2C);
  if (!stored?.refresh_token) return false;
  const storedEmail = stored.boundIdentityEmail?.trim().toLowerCase();
  return !!storedEmail && storedEmail === wantEmail;
}

/**
 * Silently mints a fresh PP access token from the persisted refresh token
 * bound to `currentIdentityEmail` — ACT-1247 SSO-PP auth-linking Phase 1
 * (design doc §5, Option A: "silent refresh on Create Auth"). NEVER opens
 * UI and never throws: resolves to `undefined` if there's no link, the
 * stash belongs to a different identity, or the refresh grant itself fails
 * (expired / revoked / invalid refresh token) — in every one of those cases
 * any dead stash is cleared and the caller (ensurePpSession in
 * MemberProfilePage, or the eligibility pre-flight) falls back to
 * PpLoginModal / "offer link". Returns the CURRENT access token with no
 * network call at all when it isn't expiring soon yet.
 */
export async function ensurePpAccessToken(
  currentIdentityEmail: string | null | undefined,
): Promise<string | undefined> {
  return (await ensurePpAccessTokenDetailed(currentIdentityEmail))?.token;
}

/**
 * A minted PP access token plus its ORIGIN — whether it came from an
 * interactive login (`ropcLogin` / PpLoginModal, which always carries the IPA
 * group claim) or a silent `ropcRefresh` grant (which can drop the claim
 * under AM-5692).
 */
export interface PpAccessToken {
  token: string;
  /** See B2cTokens.fromInteractiveLogin — true for a PpLoginModal/ropcLogin
   *  token, false for a silent-refresh-derived one. */
  fromInteractiveLogin: boolean;
}

/**
 * Same silent-mint contract as `ensurePpAccessToken` (never opens UI, never
 * throws, identity-guarded, fail-closed on a failed refresh grant), but also
 * reports the token's ORIGIN so the Create-Auth eligibility pre-flight can
 * distinguish an ambiguous refresh-derived 401 (AM-5692 → re-link) from a
 * fresh-interactive-login 401 (genuine ineligibility → confirmed denial, no
 * re-link loop; AM-5706). See members/usePpCreateAuthPreflight.ts.
 */
export async function ensurePpAccessTokenDetailed(
  currentIdentityEmail: string | null | undefined,
): Promise<PpAccessToken | undefined> {
  if (!isPpB2cConfigured || !ppB2cConfig) return undefined;
  const wantEmail = currentIdentityEmail?.trim().toLowerCase();
  if (!wantEmail) return undefined;

  const stored = getStoredTokens(PROVIDER_PORTAL_B2C);
  if (!stored) return undefined;
  const storedEmail = stored.boundIdentityEmail?.trim().toLowerCase();
  if (!storedEmail || storedEmail !== wantEmail) return undefined;

  const fresh = freshOrUndefined(stored.access_token);
  if (fresh) {
    // Serving the stashed token unchanged — its origin is whatever last wrote
    // the stash (ropcLogin → true, ropcRefresh → false). Absent flag (a stash
    // written before this field existed) is treated as NOT interactive, the
    // conservative branch (re-link, never a false confirmed denial).
    return { token: fresh, fromInteractiveLogin: stored.fromInteractiveLogin === true };
  }

  if (!stored.refresh_token) return undefined;
  try {
    const refreshed = await ropcRefresh(ppB2cConfig, stored.refresh_token);
    const token = freshOrUndefined(refreshed.access_token);
    if (!token) return undefined;
    // We just refreshed — this token is refresh-derived by construction.
    return { token, fromInteractiveLogin: false };
  } catch {
    // Refresh grant failed (expired/revoked/invalid) — drop the dead stash
    // so the next attempt doesn't keep retrying a token B2C already
    // rejected; caller falls back to PpLoginModal to re-link.
    clearStoredTokens(PROVIDER_PORTAL_B2C);
    return undefined;
  }
}

/**
 * Drops the persisted PP link stash IFF it is bound to `currentIdentityEmail`.
 *
 * Used by the Create-Auth eligibility pre-flight when a SILENTLY-minted PP
 * access token is rejected by APIM with an auth error (HTTP 401/403) during
 * the GetAuthMember check. That rejection is AMBIGUOUS: it can mean a
 * genuinely ineligible account OR a refresh-derived token that is simply
 * missing its `QA.ProviderPortal.<IPA>` group claim (known B2C bug AM-5692 —
 * refreshed tokens can drop the group claim; the policy fix, portalManager
 * #36191, may not be deployed). Rather than durably caching "not eligible"
 * off that ambiguous signal, we discard the stale stash so the next attempt
 * falls to the re-link path (PpLoginModal / "offer link"), which mints a
 * FRESH (non-refresh) token that carries the group claim — decoupling the
 * feature from the AM-5692 policy-deploy timing.
 *
 * Identity-guarded (mirrors `getPpToken`/`ensurePpAccessToken`): a 401 seen
 * while identity A is signed in can NEVER wipe identity B's stash. No-op if
 * there is no stash, or it is bound to a different identity. Analogous to the
 * dead-stash clear `ensurePpAccessToken` already does on a failed refresh
 * grant — it does NOT weaken the durable refresh-token persistence, it only
 * evicts a stash APIM just rejected.
 */
export function invalidatePpLink(currentIdentityEmail: string | null | undefined): void {
  if (!isPpB2cConfigured) return;
  const wantEmail = currentIdentityEmail?.trim().toLowerCase();
  if (!wantEmail) return;
  const stored = getStoredTokens(PROVIDER_PORTAL_B2C);
  if (!stored) return;
  const storedEmail = stored.boundIdentityEmail?.trim().toLowerCase();
  if (!storedEmail || storedEmail !== wantEmail) return;
  clearStoredTokens(PROVIDER_PORTAL_B2C);
}

/** Trims accountLabel into an email-ish string, or undefined if blank/absent. */
export function resolveIdentityEmail(
  accountLabel: string | null | undefined,
): string | undefined {
  const trimmed = accountLabel?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Resolves which bearer to decode for PP-API's `IsUserValid` identity check
 * (`AuthMemberController` compares the request body's `UserId` against the
 * *bearer's* `sub` claim) — MUST mirror MemberProfilePage.tsx's
 * `resolvedAccessToken` fallback chain in every branch, MINUS the
 * pending-sentinel substitution (the sentinel authenticates as no one, so
 * there's no real identity to decode in that window). AM-5713 (PR #217
 * blocker fix): hardcoding this to `ppToken` alone regressed the
 * not-configured/unlinked fallback branch, where `resolvedAccessToken` sends
 * the real ambient `token` but a `ppToken`-only derivation returned
 * `undefined` for `UserId` — bearer and UserId diverged.
 *
 * `hasPpLink` (a caller-supplied `hasPersistedPpLink(identityEmail)` result —
 * see that function above) mirrors `resolvedAccessToken`'s `ppPreflight.hasLink`
 * gate (added alongside the ACT-1247 SSO-PP create-auth-lookups-401 fix):
 * a linked identity with no `ppToken` YET must resolve to `undefined` here
 * too, not fall through to the ambient token — otherwise a persona-downgraded
 * (`ppEligible: false`) but still-PP-linked identity would decode a NON-PP
 * bearer's `sub`/`oid` as `UserId` and hand it to `checkPatientEligibility`,
 * a guaranteed bearer/UserId mismatch that could spuriously invalidate a
 * perfectly good PP link for a reason unrelated to real PP eligibility.
 *
 * Pure function (no I/O) so it's unit-testable without mounting
 * MemberProfilePage. Callers pass the exact same `ppToken`/`hasPpLink`/
 * `ppEligible`/`token` values `resolvedAccessToken` uses — cross-check
 * against that expression if this ever changes.
 */
export function resolveCreateAuthIdentityToken(
  ppToken: string | undefined,
  hasPpLink: boolean,
  ppEligible: boolean,
  ambientToken: string | null | undefined,
): string | undefined {
  return ppToken ?? (hasPpLink || ppEligible ? undefined : (ambientToken ?? undefined));
}
